// An order changed by its seller before it is sent (aldoo-na/store/order-modify):
// a line's quantity set (150 shoes meant as 15), a line taken out, another
// product put in (no red t-shirt, a blue one instead) — found by the search,
// which looks through the seller's own products (every product for the
// platform admin). The buyer gets a letter with what changed and the seller's
// words; the delivery fee stays as chosen, the total follows the goods.
import React, { useEffect, useMemo, useRef, useState } from "react"
import Modal from "aldoo-ra/Modal"
import Button from "aldoo-ra/Button"
import Input from "aldoo-ra/Input"
import DropdownSelect from "aldoo-ra/DropdownSelect"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import { MagnifyingGlassIcon, MinusIcon, PlusIcon, TrashIcon, ArrowUturnLeftIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline"
import { twMerge } from "tailwind-merge"
import { describeLine } from "aldoo-ra/Store/lines"
import { money, Thumb, errorMessage } from "./ui"

const MAX_QUANTITY = 9999
const clamp = (n) => Math.max(0, Math.min(MAX_QUANTITY, Math.floor(Number(n) || 0)))

/** Why the server refused the change, in the dialog's words (its own message for anything else). */
export const refusal = (res, t) => {
  if (res.error === "out_of_stock") return t("Not enough in stock: %s", String(res.message || "").replace(/^[^:]*:\s*/, ""))
  const said = {
    waybill_made: t`A waybill was made for this order: cancel it first`,
    cancel_requested: t`The buyer asked to cancel this order: answer the request first`,
    not_open: t`Only an order waiting to be sent can be modified`,
    paid_online: t`Only an order paid on delivery can be modified`,
    empty: t`An order needs at least one item: cancel it instead.`,
    unchanged: t`Nothing was changed`,
  }[res.error]
  return said || errorMessage(res)
}

/** The order's lines as rows of the dialog: what each is now, and what it was. */
const rowsOf = (order) =>
  (order?.cart || []).map((line, index) => ({
    id: `c${index}`,
    cartIndex: index,
    pid: line.pid,
    sku: line.sku || "",
    name: describeLine(line, line.name || line.pid),
    thumbnail: line.thumbnail || null,
    price: line.price || 0,
    quantity: line.quantity ?? 1,
    was: line.quantity ?? 1,
  }))

/** A quantity: − the number + (0 takes the line out). */
export function Stepper({ value, onChange, testId }) {
  return (
    <div className="inline-flex items-center rounded-full border border-admin_bordered bg-admin_paper" data-testid={testId}>
      <button type="button" className="p-1.5 text-admin_text_secondary hover:text-admin_text disabled:opacity-40" onClick={() => onChange(clamp(value - 1))} disabled={value <= 0} aria-label="decrease">
        <MinusIcon className="w-4 h-4" />
      </button>
      <input
        type="number"
        min={0}
        max={MAX_QUANTITY}
        value={value}
        onChange={(e) => onChange(clamp(e.target.value))}
        className="w-14 text-center text-sm tabular-nums bg-transparent outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
      />
      <button type="button" className="p-1.5 text-admin_text_secondary hover:text-admin_text disabled:opacity-40" onClick={() => onChange(clamp(value + 1))} disabled={value >= MAX_QUANTITY} aria-label="increase">
        <PlusIcon className="w-4 h-4" />
      </button>
    </div>
  )
}

/** One product the search found: its picture, name, price and stock, the variant to take, Add. */
export function Found({ product, t, currency, onAdd }) {
  const variants = product.variants || []
  const [sku, setSku] = useState(variants.find((variant) => variant.inStock)?.sku || variants[0]?.sku || "")
  const variant = variants.find((entry) => entry.sku === sku) || null
  const price = variant ? variant.price : product.price
  const stock = variant ? variant.stock : product.stock
  const inStock = variant ? variant.inStock : product.inStock
  const options = variants.map((entry) => ({ value: entry.sku, name: `${entry.label}${entry.inStock ? "" : ` · ${t`out of stock`}`}` }))
  return (
    <div className="flex items-center gap-3 py-2" data-testid="modify-found">
      <Thumb src={product.thumbnail} name={product.name} className="w-10 h-10 rounded" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-admin_text truncate">{product.name}</div>
        <div className="text-xs text-admin_text_secondary">
          {money(price, currency)}
          {Number.isFinite(stock) && <> · {t("%s in stock", stock)}</>}
          {!inStock && <span className="text-admin_error"> · {t`out of stock`}</span>}
          {/* the platform admin may put another seller's product in: said, so it is not done by mistake */}
          {!product.ownSeller && product.seller && <span className="text-admin_warning"> · {product.seller}</span>}
        </div>
      </div>
      {variants.length > 0 && (
        <div className="w-40 shrink-0">
          <DropdownSelect variant="admin" options={options} value={options.filter((option) => option.value === sku)} onChange={(value) => setSku((Array.isArray(value) ? value[0] : value)?.value || "")} />
        </div>
      )}
      <Button variant="admin_secondary" onClick={() => onAdd({ ...product, sku: variant?.sku || "", label: variant ? `${product.name} (${variant.label})` : product.name, price, stock })} data-testid="modify-add">
        {t`Add`}
      </Button>
    </div>
  )
}

/**
 * `order` as the order page has it (with `modifiable`); `onModify({ lines, message })`
 * saves and answers `{ error, message }` or the result; `search(term)` → `{ products }`
 * (none: products cannot be added); `onClose(done)`.
 */
export default function ModifyDialog({ order, t, onClose, onModify, search = null, height = null }) {
  const currency = order?.currency
  const [rows, setRows] = useState(() => rowsOf(order))
  const [message, setMessage] = useState("")
  const [term, setTerm] = useState("")
  const [found, setFound] = useState([])
  const [searching, setSearching] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState("")
  const asked = useRef(0)

  // the search, a moment after the typing stops; an answer to an older term is dropped
  useEffect(() => {
    const q = term.trim()
    if (!search || q.length < 2) return setFound([])
    const ask = ++asked.current
    setSearching(true)
    const timer = setTimeout(async () => {
      const res = await search(q)
      if (ask !== asked.current) return
      setSearching(false)
      setFound(res?.products || [])
    }, 300)
    return () => clearTimeout(timer)
  }, [term, search])

  const setQuantity = (id, quantity) => setRows((list) => list.map((row) => (row.id === id ? { ...row, quantity } : row)))
  // a line taken out stays on the list, struck through, until it is put back; a new one just goes
  const remove = (row) => (row.cartIndex === undefined ? setRows((list) => list.filter((entry) => entry.id !== row.id)) : setQuantity(row.id, 0))
  const add = (product) => {
    const id = `n${product.pid}|${product.sku || ""}`
    setRows((list) => {
      // the same product (and size) already on the order: one more of it
      const same = list.find((row) => row.pid === product.pid && (row.sku || "") === (product.sku || ""))
      if (same) return list.map((row) => (row.id === same.id ? { ...row, quantity: clamp(row.quantity + 1) } : row))
      return [...list, { id, pid: product.pid, sku: product.sku || "", name: product.label, thumbnail: product.thumbnail, price: product.price, quantity: 1, was: 0, stock: product.stock, seller: product.ownSeller ? "" : product.seller }]
    })
    setTerm("")
    setFound([])
  }

  const goods = rows.reduce((sum, row) => sum + Math.round(row.price * row.quantity), 0)
  const goodsBefore = (order?.cart || []).reduce((sum, line) => sum + Math.round((line.price || 0) * (line.quantity ?? 1)), 0)
  const discount = Math.min(order?.discountAmount || 0, goods)
  const shipping = order?.shippingCost || 0
  const total = goods + shipping - discount
  const changed = rows.some((row) => row.quantity !== row.was)
  const empty = rows.every((row) => row.quantity === 0)

  // what changed against the order as placed: each line's units before → after
  const changes = rows.filter((row) => row.quantity !== row.was)
  const changeText = (row) => (row.quantity === 0 ? t`taken out` : row.was === 0 ? t("added, %s pcs", row.quantity) : `${row.was} → ${row.quantity}`)

  // Save asks first: the changes, the new total, that the buyer is mailed
  const confirm = () => {
    if (!changed || empty || busy) return
    showPromptDialog({
      variant: "admin",
      title: t("Modify order %s", order?.orderId || ""),
      message: (
        <div className="flex flex-col gap-3" data-testid="modify-confirm">
          <span>{t`These changes are saved and the buyer is told by email:`}</span>
          <div className="flex flex-col divide-y divide-admin_bordered rounded-md border border-admin_bordered text-sm">
            {changes.map((row) => (
              <div key={row.id} className="flex items-center justify-between gap-3 px-3 py-2">
                <span className="min-w-0 truncate text-admin_text">{row.name}</span>
                <span className={twMerge("shrink-0 tabular-nums", row.quantity === 0 ? "text-admin_error" : row.was === 0 ? "text-admin_success" : "text-admin_text")}>{changeText(row)}</span>
              </div>
            ))}
          </div>
          <div className="flex justify-between text-sm font-semibold text-admin_text">
            <span>{t`To collect on delivery`}</span>
            <span className="tabular-nums">
              {total !== order?.amount && <span className="mr-2 font-normal text-admin_text_secondary line-through">{money(order?.amount || 0, currency)}</span>}
              {money(total, currency)}
            </span>
          </div>
          {message.trim() && <div className="rounded-md bg-admin_neutral_1/60 px-3 py-2 text-sm text-admin_text whitespace-pre-line">{message.trim()}</div>}
        </div>
      ),
      confirmText: t`Save changes`,
      cancelText: t`Back`,
      onConfirm: () => {
        submit()
      },
    })
  }

  const submit = async () => {
    if (!changed || empty || busy) return
    setBusy(true)
    setError("")
    const lines = rows
      .filter((row) => row.cartIndex !== undefined || row.quantity > 0)
      .map((row) => (row.cartIndex !== undefined ? { cartIndex: row.cartIndex, quantity: row.quantity } : { pid: row.pid, ...(row.sku ? { sku: row.sku } : {}), quantity: row.quantity }))
    const res = await onModify({ lines, message: message.trim() })
    setBusy(false)
    const failed = res?.error ? refusal(res, t) : ""
    if (failed) return setError(failed)
    onClose?.(res)
  }

  const canAdd = !!search && order?.modifiable?.canAdd !== false

  return (
    <Modal
      isOpen
      onClose={() => onClose?.()}
      variant="admin_compact"
      title={t("Modify order %s", order?.orderId || "")}
      containerStyle={height ? { height: `${Math.round(height)}px` } : undefined}
      footerContent={
        <div className="flex flex-wrap items-center gap-4">
          <Button variant="admin_primary" className="ml-auto" onClick={confirm} disabled={busy || !changed || empty} data-testid="modify-submit">
            {busy ? t`Saving…` : t`Save changes`}
          </Button>
        </div>
      }
      styleOverrides={{
        container: "bg-admin_paper border-admin_bordered w-full h-[100dvh] max-h-[100dvh] md:h-auto md:max-w-[900px] md:max-h-[94vh] overflow-hidden rounded-none md:rounded-md border shadow-lg",
        footer: "px-5 py-3 border-t border-admin_bordered bg-admin_paper",
      }}
    >
      <div className="flex flex-col gap-4 p-5 pt-3" data-testid="modify-dialog">
        <p className="m-0 text-sm text-admin_text_secondary">{t`Change quantities, take items out or add others. The buyer gets an email with the changes and your message. The delivery fee stays as chosen.`}</p>

        {/* products to put in */}
        {canAdd && (
          <div className="flex flex-col gap-1">
            <Input variant="admin" icon={<MagnifyingGlassIcon className="w-4 h-4 text-admin_text_secondary" />} placeholder={t`Search products to add…`} value={term} onChange={(e) => setTerm(e.target.value)} data-testid="modify-search" />
            {searching && <div className="text-xs text-admin_text_secondary px-1">{t`Searching…`}</div>}
            {!searching && term.trim().length >= 2 && !found.length && <div className="text-xs text-admin_text_secondary px-1">{t`No products found`}</div>}
            {found.length > 0 && (
              <div className="flex flex-col divide-y divide-admin_bordered rounded-md border border-admin_bordered px-3 max-h-72 overflow-y-auto">
                {found.map((product) => (
                  <Found key={product.pid} product={product} t={t} currency={currency} onAdd={add} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* the order's lines, and the ones put in */}
        <div className="flex flex-col divide-y divide-admin_bordered border-y border-admin_bordered">
          {rows.map((row) => {
            const out = row.quantity === 0
            const isNew = row.cartIndex === undefined
            const short = isNew && Number.isFinite(row.stock) && row.quantity > row.stock
            return (
              <div key={row.id} className={twMerge("flex items-center gap-3 py-2.5", out && "opacity-60")} data-testid="modify-line">
                <Thumb src={row.thumbnail} name={row.name} className="w-10 h-10 rounded" />
                <div className="flex-1 min-w-0">
                  <div className={twMerge("text-sm font-medium text-admin_text", out && "line-through")}>{row.name}</div>
                  <div className="text-xs text-admin_text_secondary">
                    <span className="break-all">{row.pid}</span>
                    {isNew && <span className="ml-2 rounded-full bg-admin_success/10 px-2 py-0.5 text-admin_success">{t`added`}</span>}
                    {!isNew && !out && row.quantity !== row.was && <span className="ml-2">{t("was %s", row.was)}</span>}
                    {out && <span className="ml-2 text-admin_error">{t`taken out`}</span>}
                    {row.seller && <span className="ml-2 text-admin_warning">{row.seller}</span>}
                    {short && (
                      <span className="ml-2 inline-flex items-center gap-1 text-admin_warning">
                        <ExclamationTriangleIcon className="w-3.5 h-3.5" />
                        {t("Only %s in stock", row.stock)}
                      </span>
                    )}
                  </div>
                </div>
                {!out && <Stepper value={row.quantity} onChange={(quantity) => setQuantity(row.id, quantity)} testId="modify-quantity" />}
                <div className="w-28 text-right text-sm tabular-nums">
                  {!out && <div className="text-admin_text">{money(Math.round(row.price * row.quantity), currency)}</div>}
                  {/* what the line was, for comparison */}
                  {row.was > 0 && row.quantity !== row.was && <div className="text-xs text-admin_text_secondary line-through">{money(Math.round(row.price * row.was), currency)}</div>}
                </div>
                {out ? (
                  <button type="button" onClick={() => setQuantity(row.id, row.was)} className="p-1.5 text-admin_text_secondary hover:text-admin_primary" title={t`Put it back`} data-testid="modify-undo">
                    <ArrowUturnLeftIcon className="w-4 h-4" />
                  </button>
                ) : (
                  <button type="button" onClick={() => remove(row)} className="p-1.5 text-admin_text_secondary hover:text-admin_error" title={t`Take it out`} data-testid="modify-remove">
                    <TrashIcon className="w-4 h-4" />
                  </button>
                )}
              </div>
            )
          })}
        </div>

        {/* the sums: the goods before and after, the fee, the discount, what the courier collects */}
        <div className="ml-auto w-full max-w-sm flex flex-col gap-1 text-sm">
          <div className="flex justify-between text-admin_text_secondary">
            <span>{t`Items`}</span>
            <span className="tabular-nums">
              {goods !== goodsBefore && <span className="mr-2 line-through">{money(goodsBefore, currency)}</span>}
              <span className="text-admin_text">{money(goods, currency)}</span>
            </span>
          </div>
          {discount > 0 && (
            <div className="flex justify-between text-admin_text_secondary">
              <span>{t`Discount`}</span>
              <span className="tabular-nums">−{money(discount, currency)}</span>
            </div>
          )}
          <div className="flex justify-between text-admin_text_secondary">
            <span>{t`Shipping`}</span>
            <span className="tabular-nums">{money(shipping, currency)}</span>
          </div>
          <div className="flex justify-between font-semibold text-admin_text">
            <span>{t`To collect on delivery`}</span>
            <span className="tabular-nums">{money(total, currency)}</span>
          </div>
        </div>

        <label className="flex flex-col gap-1">
          <span className="text-xs text-admin_text_secondary">{t`Message to the buyer`}</span>
          <textarea
            rows={3}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={t`E.g. We are out of the red one, so we put in the blue one in the same size.`}
            className="px-3 py-2 rounded-md border text-sm outline-none resize-y border-admin_bordered bg-admin_paper text-admin_text"
            data-testid="modify-message"
          />
        </label>

        {empty && <div className="text-sm text-admin_error">{t`An order needs at least one item: cancel it instead.`}</div>}
        {error && (
          <div className="flex items-start gap-2 rounded-md border border-admin_error/40 bg-admin_error/10 p-3 text-sm text-admin_text" data-testid="modify-error">
            <ExclamationTriangleIcon className="w-5 h-5 flex-shrink-0 text-admin_error" />
            <span>{error}</span>
          </div>
        )}
      </div>
    </Modal>
  )
}
